import React from 'react';
import { Container, Row, Col, Card, Table, Badge, Form, Button, Spinner, Tabs, Tab, Pagination } from 'react-bootstrap';
import {
  EyeIcon,
  GlobeAltIcon,
  UserGroupIcon,
  ArrowPathIcon,
  FunnelIcon,
  MapPinIcon,
  ClockIcon,
  ComputerDesktopIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/solid';
import Header from '../common/Header';
import { useAdminActivityLogs } from './hooks/useAdminActivityLogs';

const ACTION_BADGES: Record<string, { bg: string; text?: string }> = {
  login: { bg: 'success' },
  logout: { bg: 'secondary' },
  login_failed: { bg: 'danger' },
  page_visit: { bg: 'info' },
  product_view: { bg: 'primary' },
  add_to_cart: { bg: 'warning', text: 'dark' },
  remove_from_cart: { bg: 'warning', text: 'dark' },
  place_order: { bg: 'success' },
  view_order: { bg: 'primary' },
  view_reports: { bg: 'info' },
  export_data: { bg: 'dark' },
  profile_update: { bg: 'secondary' },
  api_request: { bg: 'light', text: 'dark' },
};

const AdminActivityLogs: React.FC = () => {
  const {
    activeTab, setActiveTab,
    logs, summaries, actionTypes,
    loading,
    totalCount, currentPage,
    setFilters,
    filterAction, filterDateFrom, filterDateTo, filterSearch, selectedUserId,
    expandedLogId, setExpandedLogId,
    fetchLogs, fetchSummaries,
    handleResolveGeo, clearFilters,
    formatDate, formatTimeAgo, parseUserAgent, parseBrowser,
    totalPages,
  } = useAdminActivityLogs();

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    
    for (let i = start; i <= end; i++) {
      pages.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => fetchLogs(i)}>
          {i}
        </Pagination.Item>
      );
    }
    
    return (
      <Pagination className="mb-0 justify-content-center" size="sm">
        <Pagination.First onClick={() => fetchLogs(1)} disabled={currentPage === 1} />
        <Pagination.Prev onClick={() => fetchLogs(currentPage - 1)} disabled={currentPage === 1} />
        {start > 1 && <Pagination.Ellipsis disabled />}
        {pages}
        {end < totalPages && <Pagination.Ellipsis disabled />}
        <Pagination.Next onClick={() => fetchLogs(currentPage + 1)} disabled={currentPage === totalPages} />
        <Pagination.Last onClick={() => fetchLogs(totalPages)} disabled={currentPage === totalPages} />
      </Pagination>
    );
  };

  return (
    <>
      <Header />
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {/* Page Title */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex align-items-center gap-2 mb-1">
              <ShieldCheckIcon style={{ width: '28px', height: '28px', color: '#1e3a8a' }} />
              <h4 className="mb-0 fw-bold" style={{ color: '#1e3a8a' }}>Activity Monitor</h4>
            </div>
            <p className="text-muted mb-0 small">
              Track user logins, page visits, orders, and all system activity
            </p>
          </Col>
        </Row>

        {/* Summary Cards */}
        <Row className="mb-4 g-3">
          <Col xs={6} md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center py-3">
                <UserGroupIcon style={{ width: '24px', height: '24px', color: '#3b82f6' }} />
                <h3 className="fw-bold mt-2 mb-0">{summaries.length}</h3>
                <small className="text-muted">Active Users</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center py-3">
                <EyeIcon style={{ width: '24px', height: '24px', color: '#10b981' }} />
                <h3 className="fw-bold mt-2 mb-0">{totalCount}</h3>
                <small className="text-muted">Total Events</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center py-3">
                <GlobeAltIcon style={{ width: '24px', height: '24px', color: '#f59e0b' }} />
                <h3 className="fw-bold mt-2 mb-0">
                  {summaries.reduce((sum, s) => sum + s.unique_ips, 0)}
                </h3>
                <small className="text-muted">Unique IPs</small>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={6} md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="text-center py-3">
                <ClockIcon style={{ width: '24px', height: '24px', color: '#ef4444' }} />
                <h3 className="fw-bold mt-2 mb-0">
                  {summaries.reduce((sum, s) => sum + s.login_count, 0)}
                </h3>
                <small className="text-muted">Total Logins</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabs */}
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'logs')} className="mb-3">
          {/* ========== ACTIVITY LOGS TAB ========== */}
          <Tab eventKey="logs" title="Activity Logs">
            {/* Filters */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Body className="py-2 px-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <FunnelIcon style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                  <small className="fw-semibold text-muted">Filters</small>
                </div>
                <Row className="g-2">
                  <Col xs={12} sm={6} md={3} lg={2}>
                    <Form.Select size="sm" value={filterAction} onChange={(e) => setFilters(prev => ({ ...prev, filterAction: e.target.value }))}>
                      <option value="">All Actions</option>
                      {actionTypes.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} sm={6} md={3} lg={2}>
                    <Form.Select size="sm" value={selectedUserId} onChange={(e) => setFilters(prev => ({ ...prev, selectedUserId: e.target.value }))}>
                      <option value="">All Users</option>
                      {summaries.map((s) => (
                        <option key={s.user_id} value={s.user_id}>
                          {s.company_name || s.username}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col xs={6} sm={4} md={2}>
                    <Form.Control 
                      size="sm" type="date" 
                      value={filterDateFrom} 
                      onChange={(e) => setFilters(prev => ({ ...prev, filterDateFrom: e.target.value }))}
                      placeholder="From"
                    />
                  </Col>
                  <Col xs={6} sm={4} md={2}>
                    <Form.Control 
                      size="sm" type="date" 
                      value={filterDateTo} 
                      onChange={(e) => setFilters(prev => ({ ...prev, filterDateTo: e.target.value }))}
                      placeholder="To"
                    />
                  </Col>
                  <Col xs={12} sm={4} md={2} lg={2}>
                    <Form.Control 
                      size="sm" type="text" 
                      placeholder="Search..."
                      value={filterSearch}
                      onChange={(e) => setFilters(prev => ({ ...prev, filterSearch: e.target.value }))}
                    />
                  </Col>
                  <Col xs={12} md={1} lg={2} className="d-flex gap-2">
                    <Button size="sm" variant="primary" onClick={() => fetchLogs(1)} className="flex-grow-1">
                      Apply
                    </Button>
                    <Button size="sm" variant="outline-secondary" onClick={clearFilters}>
                      Clear
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Logs Table */}
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">Loading activity logs...</p>
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <EyeIcon style={{ width: '48px', height: '48px', opacity: 0.3 }} />
                    <p className="mt-2">No activity logs found</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                        <thead style={{ backgroundColor: '#f8fafc' }}>
                          <tr>
                            <th style={{ width: '160px' }}>Time</th>
                            <th>User</th>
                            <th>Action</th>
                            <th className="d-none d-md-table-cell">Path</th>
                            <th className="d-none d-lg-table-cell">IP / Location</th>
                            <th className="d-none d-xl-table-cell">Device</th>
                            <th style={{ width: '40px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map((log) => (
                            <React.Fragment key={log.id}>
                              <tr 
                                style={{ cursor: 'pointer' }}
                                onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)}
                              >
                                <td>
                                  <div style={{ fontSize: '0.8rem' }}>{formatDate(log.timestamp)}</div>
                                  <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{formatTimeAgo(log.timestamp)}</div>
                                </td>
                                <td>
                                  <div className="fw-semibold">{log.user_display}</div>
                                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                    @{log.username_snapshot}
                                    {log.company_name && <> &middot; {log.company_name}</>}
                                  </div>
                                </td>
                                <td>
                                  <Badge 
                                    bg={ACTION_BADGES[log.action_type]?.bg || 'secondary'}
                                    text={ACTION_BADGES[log.action_type]?.text as any}
                                  >
                                    {log.action_display}
                                  </Badge>
                                </td>
                                <td className="d-none d-md-table-cell">
                                  <span style={{ fontSize: '0.8rem', color: '#4b5563' }}>
                                    {log.path.length > 40 ? log.path.slice(0, 40) + '...' : log.path}
                                  </span>
                                </td>
                                <td className="d-none d-lg-table-cell">
                                  <div style={{ fontSize: '0.8rem' }}>{log.ip_address || '-'}</div>
                                  {log.geo_resolved && log.geo_city && (
                                    <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                      <MapPinIcon style={{ width: '10px', height: '10px', display: 'inline' }} />{' '}
                                      {log.geo_city}, {log.geo_country}
                                    </div>
                                  )}
                                </td>
                                <td className="d-none d-xl-table-cell">
                                  <div style={{ fontSize: '0.75rem' }}>
                                    <ComputerDesktopIcon style={{ width: '12px', height: '12px', display: 'inline' }} />{' '}
                                    {parseUserAgent(log.user_agent)} / {parseBrowser(log.user_agent)}
                                  </div>
                                </td>
                                <td>
                                  <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                    {expandedLogId === log.id ? '▲' : '▼'}
                                  </span>
                                </td>
                              </tr>
                              {/* Expanded Details Row */}
                              {expandedLogId === log.id && (
                                <tr>
                                  <td colSpan={7} style={{ backgroundColor: '#f9fafb', padding: '12px 20px' }}>
                                    <Row className="g-3" style={{ fontSize: '0.8rem' }}>
                                      <Col xs={12} md={4}>
                                        <strong>Full Details</strong>
                                        <pre style={{ 
                                          fontSize: '0.75rem', 
                                          backgroundColor: '#fff', 
                                          padding: '8px', 
                                          borderRadius: '6px',
                                          maxHeight: '150px',
                                          overflow: 'auto',
                                          border: '1px solid #e5e7eb'
                                        }}>
                                          {JSON.stringify(log.details, null, 2)}
                                        </pre>
                                      </Col>
                                      <Col xs={12} md={4}>
                                        <strong>Location</strong>
                                        <div>Country: {log.geo_country || 'Unknown'}</div>
                                        <div>Region: {log.geo_region || 'Unknown'}</div>
                                        <div>City: {log.geo_city || 'Unknown'}</div>
                                        <div>ISP: {log.geo_isp || 'Unknown'}</div>
                                        {log.geo_lat && log.geo_lon && (
                                          <div className="mt-1">
                                            <a 
                                              href={`https://maps.google.com/?q=${log.geo_lat},${log.geo_lon}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{ fontSize: '0.75rem' }}
                                            >
                                              View on Map ↗
                                            </a>
                                          </div>
                                        )}
                                      </Col>
                                      <Col xs={12} md={4}>
                                        <strong>Technical</strong>
                                        <div>Method: {log.method}</div>
                                        <div>Session: {log.session_id ? log.session_id.slice(0, 20) + '...' : 'N/A'}</div>
                                        <div className="mt-1">
                                          <strong>User Agent</strong>
                                          <div style={{ 
                                            fontSize: '0.7rem', 
                                            wordBreak: 'break-all', 
                                            color: '#6b7280',
                                            maxHeight: '60px',
                                            overflow: 'auto'
                                          }}>
                                            {log.user_agent || 'N/A'}
                                          </div>
                                        </div>
                                      </Col>
                                    </Row>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <div className="d-flex justify-content-between align-items-center px-3 py-2" 
                         style={{ borderTop: '1px solid #e5e7eb', fontSize: '0.8rem' }}>
                      <span className="text-muted">
                        Showing {(currentPage - 1) * 50 + 1}-{Math.min(currentPage * 50, totalCount)} of {totalCount}
                      </span>
                      {renderPagination()}
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* ========== USER SUMMARY TAB ========== */}
          <Tab eventKey="users" title="User Summary">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                    <thead style={{ backgroundColor: '#f8fafc' }}>
                      <tr>
                        <th>Customer</th>
                        <th>ID</th>
                        <th>Logins</th>
                        <th>Page Visits</th>
                        <th>Orders</th>
                        <th className="d-none d-md-table-cell">Unique IPs</th>
                        <th>Last Login</th>
                        <th className="d-none d-md-table-cell">Last Login Location</th>
                        <th>Last Activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaries.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center py-4 text-muted">
                            No user activity data yet. Activity will appear after users interact with the system.
                          </td>
                        </tr>
                      ) : (
                        summaries.map((s) => (
                          <tr key={s.user_id}>
                            <td>
                              <div className="fw-semibold">{s.company_name || s.username}</div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{s.username}</div>
                            </td>
                            <td>
                              <Badge bg="light" text="dark" style={{ fontSize: '0.75rem' }}>{s.customer_id}</Badge>
                            </td>
                            <td>{s.login_count}</td>
                            <td>{s.page_visits}</td>
                            <td>{s.orders_placed}</td>
                            <td className="d-none d-md-table-cell">{s.unique_ips}</td>
                            <td>
                              <div style={{ fontSize: '0.8rem' }}>{formatTimeAgo(s.last_login)}</div>
                              {s.last_login && (
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                                  {formatDate(s.last_login)}
                                </div>
                              )}
                            </td>
                            <td className="d-none d-md-table-cell">
                              {s.last_login_city ? (
                                <div style={{ fontSize: '0.8rem' }}>
                                  <MapPinIcon style={{ width: '12px', height: '12px', display: 'inline', color: '#6b7280' }} />{' '}
                                  {s.last_login_city}, {s.last_login_country}
                                  <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>IP: {s.last_login_ip}</div>
                                </div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <div style={{ fontSize: '0.8rem' }}>{formatTimeAgo(s.last_activity)}</div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        {/* Action Buttons */}
        <div className="d-flex gap-2 mt-3">
          <Button 
            size="sm" 
            variant="outline-secondary"
            onClick={handleResolveGeo}
            className="d-flex align-items-center gap-1"
          >
            <GlobeAltIcon style={{ width: '14px', height: '14px' }} />
            Resolve Locations
          </Button>
          <Button 
            size="sm" 
            variant="outline-secondary"
            onClick={() => { fetchLogs(currentPage); fetchSummaries(); }}
            className="d-flex align-items-center gap-1"
          >
            <ArrowPathIcon style={{ width: '14px', height: '14px' }} />
            Refresh
          </Button>
        </div>
      </Container>
    </>
  );
};

export default AdminActivityLogs;
